import { typedSignatureDataUrl } from '../../../harleys-pipeline/packages/design-system/src/signature';
import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Button,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowReset20Regular, Send20Regular } from '@fluentui/react-icons';
import { AscoraJobSummary, AuthenticatedUser, M365Profile, Take5Submission } from '../types';
import { ReadOnlyField } from './ReadOnlyField';
import { buildPdfFileName, formatDateAU, formatTimeAU } from '../services/risk';

interface Props {
  job: AscoraJobSummary;
  user: AuthenticatedUser;
  profile: M365Profile | null;
  submitting: boolean;
  onSubmit: (signOff: Take5Submission['signOff']) => void;
}

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '16px', paddingBottom: '20px', paddingLeft: '18px', paddingRight: '18px' },
  section: {
    marginBottom: '14px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNum: {
    backgroundColor: '#231F20',
    color: '#FFFFFF',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  fieldGrid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  declaration: { fontSize: '13px', lineHeight: 1.5 },
  compactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
    marginTop: '12px',
  },
  sigArea: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    borderTopWidth: '2px',
    borderRightWidth: '2px',
    borderBottomWidth: '2px',
    borderLeftWidth: '2px',
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    height: '168px',
    position: 'relative',
    overflow: 'hidden',
    touchAction: 'none',
  },
  sigCanvas: { width: '100% !important', height: '100% !important' },
  sigHint: {
    position: 'absolute',
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '13px',
    color: tokens.colorNeutralForeground4,
    pointerEvents: 'none',
  },
  sigActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
  },
  waNote: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  submitPreview: {
    backgroundColor: '#E8F5E9',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#A5D6A7',
    borderRightColor: '#A5D6A7',
    borderBottomColor: '#A5D6A7',
    borderLeftColor: '#A5D6A7',
    borderRadius: '18px',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '14px',
    paddingRight: '14px',
    marginBottom: '12px',
  },
  previewTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1B5E20',
    marginBottom: '6px',
  },
  previewItem: {
    fontSize: '12.5px',
    color: tokens.colorNeutralForeground2,
    paddingTop: '4px',
    paddingBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    '::before': {
      content: "'✓'",
      color: '#1B5E20',
      fontWeight: 700,
      fontSize: '13px',
    },
  },
  previewCode: {
    backgroundColor: '#FFFFFF',
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '7px',
    paddingRight: '7px',
    borderRadius: '3px',
    fontSize: '11px',
    color: '#6F0F12',
    fontFamily: 'ui-monospace, monospace',
  },
  cta: {
    width: '100%',
    marginTop: '8px',
    minHeight: '52px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '18px',
    boxShadow: '0 10px 22px rgba(227,39,45,0.18)',
  },
});

export function SignOffScreen({ job, user, profile, onSubmit, submitting }: Props) {
  const styles = useStyles();
  const sigRef = useRef<SignatureCanvas | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasSig, setHasSig] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const now = new Date();
  const pdfName = buildPdfFileName(job.jobNumber);

  const positionTitle = profile?.positionTitle || '';
  const department = profile?.department || '';
  const managerName = profile?.managerName || '';
  const mobilePhone = profile?.mobilePhone || '';

  const clearSig = () => {
    sigRef.current?.clear();
    setHasSig(false);
  };

  const handleSubmit = () => {
    if (submitting) return;
    setValidationError(null);
    if ((!sigRef.current || sigRef.current.isEmpty()) && typedSignature.trim().length < 2) {
      setValidationError('Draw your signature or type your full name before submitting.');
      return;
    }
    if (!positionTitle) {
      setValidationError('Your workplace position title is missing. Contact your supervisor before submitting.');
      return;
    }
    const signatureDataUrl = sigRef.current && !sigRef.current.isEmpty()
      ? sigRef.current.getTrimmedCanvas().toDataURL('image/png')
      : typedSignatureDataUrl(typedSignature);
    onSubmit({
      workerName: user.name,
      workerEmail: user.email,
      workerMobile: mobilePhone || undefined,
      positionTitle,
      department: department || undefined,
      managerName: managerName || undefined,
      signedAt: new Date().toISOString(),
      signatureDataUrl,
    });
  };

  return (
    <div className={styles.body} data-screen-body>
      <Card className={styles.section}>
        <div className={styles.sectionTitle}>
          <span className={styles.stepNum}>8</span>
          Worker declaration
        </div>
        <div className={styles.declaration}>
          I, <strong>{user.name}</strong>, have read this Take 5, understand the hazards and controls identified above, and will stop work immediately if conditions become unsafe.
        </div>
        <div className={styles.compactGrid}>
          <div className={styles.fieldGrid2}>
            <ReadOnlyField label="Worker" value={user.name} source="M365" />
            <ReadOnlyField
              label="Position title"
              value={positionTitle || <em style={{ color: tokens.colorNeutralForeground3 }}>not set</em>}
              source={positionTitle ? 'M365' : undefined}
            />
          </div>
          <div className={styles.fieldGrid2}>
            <ReadOnlyField
              label="Department"
              value={department || '—'}
              source={department ? 'M365' : undefined}
            />
            <ReadOnlyField
              label="Manager"
              value={managerName || '—'}
              source={managerName ? 'M365' : undefined}
            />
          </div>
          <div className={styles.fieldGrid2}>
            <ReadOnlyField
              label="Mobile"
              value={mobilePhone || <em style={{ color: tokens.colorNeutralForeground3 }}>not set</em>}
              source={mobilePhone ? 'M365' : undefined}
            />
            <ReadOnlyField
              label="Date & time"
              value={`${formatDateAU(now)} · ${formatTimeAU(now)}`}
              source="AUTO"
            />
          </div>
        </div>
      </Card>

      {!mobilePhone && (
        <MessageBar intent="warning" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            Your mobile number isn't set in M365. The PDF and audit record will be created without it.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card className={styles.section}>
        <div className={styles.sectionTitle}>
          <span className={styles.stepNum}>9</span>
          Signature
        </div>
        <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, marginBottom: 10 }}>
          Draw your signature with your finger.
        </div>
        <div className={styles.sigArea}>
          {!hasSig && <div className={styles.sigHint}>Sign here</div>}
          <SignatureCanvas
            ref={sigRef}
            penColor="#231F20"
            onEnd={() => setHasSig(Boolean(sigRef.current && !sigRef.current.isEmpty()))}
            canvasProps={{ className: styles.sigCanvas }}
          />
        </div>
        <Field label="Or type your full name">
          <Input value={typedSignature} onChange={(_, data) => { setTypedSignature(data.value); setValidationError(null); }} autoComplete="name" maxLength={200} />
        </Field>
        <div className={styles.sigActions}>
          <span className={styles.waNote}>Your signature is saved with the safety check</span>
          <Button
            appearance="outline"
            size="medium"
            icon={<ArrowReset20Regular />}
            onClick={clearSig}
          >
            Clear
          </Button>
        </div>
      </Card>

      <div className={styles.submitPreview}>
        <div className={styles.previewTitle}>On submit we'll:</div>
        <div className={styles.previewItem}>Generate PDF <code className={styles.previewCode}>{pdfName}</code></div>
        <div className={styles.previewItem}>Attach PDF to Ascora job</div>
        <div className={styles.previewItem}>Log audit record to WHS Hub</div>
      </div>

      {validationError && <MessageBar intent="error" role="alert"><MessageBarBody>{validationError}</MessageBarBody></MessageBar>}
      <Button
        disabled={submitting}
        appearance="primary"
        size="large"
        icon={<Send20Regular />}
        onClick={handleSubmit}
        className={styles.cta}
      >
        Submit & attach to Ascora
      </Button>
    </div>
  );
}
